import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  AlertCircle,
  Check,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';

const ImageUpload = ({ 
  currentImage, 
  onImageUpload, 
  onImageRemove,
  disabled = false,
  maxSize = 5 * 1024 * 1024, // 5MB
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Validate file
  const validateFile = (file) => {
    if (!acceptedTypes.includes(file.type)) {
      throw new Error(`Invalid file type. Please upload: ${acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}`);
    }

    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      throw new Error(`File too large. Maximum size is ${maxSizeMB}MB`);
    }

    return true;
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    try {
      validateFile(file);
      
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      
      // Start upload
      setUploading(true);
      setUploadProgress(0);
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Upload file
      await onImageUpload(file);
      
      // Complete progress
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Clean up
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.message || 'Failed to upload image');
      
      // Reset preview on error
      setPreview(currentImage || null);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploading) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleInputChange = (e) => {
    if (disabled || uploading) return;
    
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Handle remove image
  const handleRemove = () => {
    if (disabled || uploading) return;
    
    setPreview(null);
    if (onImageRemove) {
      onImageRemove();
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Open file dialog
  const openFileDialog = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <motion.div
        className={`
          relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 cursor-pointer
          ${dragActive 
            ? 'border-primary-teal bg-primary-teal/5' 
            : preview 
              ? 'border-neutral-200 bg-neutral-50' 
              : 'border-neutral-300 hover:border-primary-teal hover:bg-primary-teal/5'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${uploading ? 'pointer-events-none' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={preview ? undefined : openFileDialog}
        whileHover={!disabled && !uploading ? { scale: 1.01 } : {}}
        whileTap={!disabled && !uploading ? { scale: 0.99 } : {}}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled || uploading}
        />

        {/* Upload Progress Overlay */}
        <AnimatePresence>
          {uploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-white/90 rounded-xl flex flex-col items-center justify-center z-10"
            >
              <Loader2 className="w-8 h-8 text-primary-teal animate-spin mb-2" />
              <p className="text-sm text-neutral-600 mb-2">Uploading image...</p>
              <div className="w-32 h-2 bg-neutral-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary-teal"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1">{uploadProgress}%</p>
            </motion.div>
          )}
        </AnimatePresence>

        {preview ? (
          /* Image Preview */
          <div className="relative">
            <img
              src={preview}
              alt="Profile preview"
              className="w-32 h-32 mx-auto rounded-full object-cover border-4 border-white shadow-medium"
            />
            
            {/* Remove button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="absolute -top-2 -right-2 w-8 h-8 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-medium"
              disabled={disabled || uploading}
            >
              <X className="w-4 h-4" />
            </Button>

            {/* Change image button */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openFileDialog();
              }}
              className="mt-4 mx-auto block border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white"
              disabled={disabled || uploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              Change Image
            </Button>
          </div>
        ) : (
          /* Upload Prompt */
          <div className="text-center">
            <motion.div
              className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                dragActive ? 'bg-primary-teal text-white' : 'bg-neutral-100 text-neutral-400'
              }`}
              animate={dragActive ? { scale: 1.1 } : { scale: 1 }}
            >
              {dragActive ? <Check className="w-8 h-8" /> : <ImageIcon className="w-8 h-8" />}
            </motion.div>
            
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              {dragActive ? 'Drop image here' : 'Upload Profile Image'}
            </h3>
            
            <p className="text-sm text-neutral-600 mb-4">
              Drag and drop an image here, or click to browse
            </p>
            
            <div className="flex flex-wrap justify-center gap-2 text-xs text-neutral-500">
              <span>Supported: {acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}</span>
              <span>•</span>
              <span>Max size: {formatFileSize(maxSize)}</span>
            </div>
          </div>
        )}
      </motion.div>

      {/* Upload Info */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Image Processing</p>
          <p>Your image will be automatically optimized and converted to WebP format for better performance.</p>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
